import React, { useState } from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { ReviewApiClient } from "@/clients/reviewApiClient";
import { toast } from "sonner";
import { userFacingErrorMessage } from '@/utils/userFacingError';
import { formatAmount } from "@financialharmony/ui";

interface JournalLine {
  id: string;
  accountNumber: string;
  debit: string;
  credit: string;
  description: string;
}

interface ManualBookkeepingPanelProps {
  suggestionId: string;
  isCompleted: boolean;
  onComplete: () => void;
}

const emptyLine = (): JournalLine => ({
  id: Math.random().toString(36).slice(2),
  accountNumber: "",
  debit: "",
  credit: "",
  description: "",
});

const parseAmount = (value: string): number => {
  const parsed = parseFloat(value.replace(",", "."));
  return isNaN(parsed) ? 0 : parsed;
};

const ManualBookkeepingPanel: React.FC<ManualBookkeepingPanelProps> = ({
  suggestionId,
  isCompleted,
  onComplete,
}) => {
  const t = useTranslations("consultantReview.manual");
  const today = new Date().toISOString().split("T")[0];
  const [description, setDescription] = useState("");
  const [vendor, setVendor] = useState("");
  const [verificationDate, setVerificationDate] = useState(today);
  const [lines, setLines] = useState<JournalLine[]>([emptyLine(), emptyLine()]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalDebit = lines.reduce((s, l) => s + parseAmount(l.debit), 0);
  const totalCredit = lines.reduce((s, l) => s + parseAmount(l.credit), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.005;
  const hasMinLines = lines.length >= 2;
  const allAccountsFilled = lines.every((l) => l.accountNumber.trim() !== "");
  const hasAnyAmount = lines.some((l) => parseAmount(l.debit) > 0 || parseAmount(l.credit) > 0);
  const canSubmit =
    description.trim() !== "" &&
    verificationDate !== "" &&
    hasMinLines &&
    allAccountsFilled &&
    hasAnyAmount &&
    isBalanced &&
    !isSubmitting;

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (id: string) =>
    setLines((prev) => (prev.length > 2 ? prev.filter((l) => l.id !== id) : prev));

  const updateLine = (id: string, field: keyof Omit<JournalLine, "id">, value: string) =>
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const result = await ReviewApiClient.submitManualSuggestion({
        suggestionId,
        description: description.trim(),
        vendor: vendor.trim() || undefined,
        verificationDate,
        journalEntries: lines.map((l) => ({
          accountNumber: l.accountNumber.trim(),
          debit: parseAmount(l.debit) || null,
          credit: parseAmount(l.credit) || null,
          description: l.description.trim() || undefined,
        })),
      });
      if (result.success) {
        toast.success(t("toasts.submitted"));
        onComplete();
      }
    } catch (error) {
      const message = userFacingErrorMessage(error, t("toasts.submitFailed"));
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = async () => {
    setIsSubmitting(true);
    try {
      const result = await ReviewApiClient.declineUnsupportedDocument(suggestionId);
      if (result.success) {
        toast.success(t("toasts.declined"));
        onComplete();
      }
    } catch (error) {
      const message = userFacingErrorMessage(error, t("toasts.declineFailed"));
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="p-4 bg-muted rounded-lg">
        <h4 className="text-sm font-semibold mb-2">{t("handledTitle")}</h4>
        <p className="text-sm text-muted-foreground">{t("handledBody")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("title")}</h3>
        <p className="text-sm text-muted-foreground">{t("intro")}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="manual-description">{t("description")}</Label>
          <Textarea
            id="manual-description"
            placeholder={t("descriptionPlaceholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[60px]"
            disabled={isSubmitting}
          />
        </div>
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="manual-vendor">{t("vendor")} <span className="text-muted-foreground font-normal">{t("optional")}</span></Label>
            <Input
              id="manual-vendor"
              placeholder={t("vendorPlaceholder")}
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
              disabled={isSubmitting}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="manual-date">{t("verificationDate")}</Label>
              <Input
                id="manual-date"
                type="date"
                value={verificationDate}
                onChange={(e) => setVerificationDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("amount")}</Label>
              <div className="flex h-9 items-center rounded border border-border bg-surface-inset px-3 text-sm text-muted-foreground">
                {totalDebit > 0 ? `${formatAmount(totalDebit)} SEK` : "—"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-[2fr_1fr_1fr_2fr_auto] gap-2 text-xs font-medium text-muted-foreground px-1">
          <span>{t("accountNo")}</span>
          <span>{t("debit")}</span>
          <span>{t("credit")}</span>
          <span>{t("lineDescription")}</span>
          <span />
        </div>

        {lines.map((line) => (
          <div key={line.id} className="grid grid-cols-[2fr_1fr_1fr_2fr_auto] gap-2 items-center">
            <Input
              placeholder={t("accountPlaceholder")}
              value={line.accountNumber}
              onChange={(e) => updateLine(line.id, "accountNumber", e.target.value)}
              disabled={isSubmitting}
            />
            <Input
              placeholder="0.00"
              value={line.debit}
              onChange={(e) => updateLine(line.id, "debit", e.target.value)}
              disabled={isSubmitting}
            />
            <Input
              placeholder="0.00"
              value={line.credit}
              onChange={(e) => updateLine(line.id, "credit", e.target.value)}
              disabled={isSubmitting}
            />
            <Input
              placeholder={t("notePlaceholder")}
              value={line.description}
              onChange={(e) => updateLine(line.id, "description", e.target.value)}
              disabled={isSubmitting}
            />
            <Button
              variant="ghost"
              size="icon"
              aria-label={t("removeLine")}
              onClick={() => removeLine(line.id)}
              disabled={lines.length <= 2 || isSubmitting}
              className="h-9 w-9 shrink-0"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <div className="grid grid-cols-[2fr_1fr_1fr_2fr_auto] gap-2 items-center text-sm font-medium px-1 pt-1 border-t">
          <span className="text-muted-foreground">{t("total")}</span>
          <span className={totalDebit > 0 ? "text-foreground" : "text-muted-foreground"}>
            {totalDebit.toFixed(2)}
          </span>
          <span className={totalCredit > 0 ? "text-foreground" : "text-muted-foreground"}>
            {totalCredit.toFixed(2)}
          </span>
          <span />
          <span />
        </div>

        {hasAnyAmount && !isBalanced && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {t("unbalanced", { difference: Math.abs(totalDebit - totalCredit).toFixed(2) })}
            </AlertDescription>
          </Alert>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={addLine}
          disabled={isSubmitting}
          className="mt-1"
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("addLine")}
        </Button>
      </div>

      <div className="border-t pt-4 space-y-2">
        <Button
          className="w-full"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting ? t("submitting") : t("submit")}
        </Button>
        <Button
          variant="destructive"
          className="w-full"
          onClick={handleDecline}
          disabled={isSubmitting}
        >
          {t("decline")}
        </Button>
      </div>
    </div>
  );
};

export default ManualBookkeepingPanel;
